import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Repeat, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Save, Clock, PenTool, Mail, Linkedin, Phone, Star, Users, Eye, Pencil, AlertTriangle, Wand2, Pause, Play, XCircle, CalendarClock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Checkbox } from '../components/ui/checkbox';
import { CcPicker } from '../components/CcPicker';
import { SignaturePreview } from '../components/CampaignDialog';
import { StepRow } from '../components/DealSequencesCard';
import { StalledOutreachCard } from '../components/StalledOutreachCard';
import { PRIO_STYLES } from '../components/PrioCriteriaPicker';

const CHANNEL_ICONS = { letter: PenTool, email: Mail, linkedin: Linkedin, call: Phone };
const CHANNEL_COLORS = {
  letter: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  email: 'text-sky-400 border-sky-500/40 bg-sky-500/10',
  linkedin: 'text-blue-400 border-blue-500/40 bg-blue-500/10',
  call: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
};

const SAMPLE_CTX = { first_name: 'Erika', last_name: 'Mustermann', title: 'Frau', full_name: 'Erika Mustermann',
  company: 'Muster GmbH & Co. KG', deal_name: 'Project Muster', anrede: 'Sehr geehrte Frau Mustermann' };
const renderSample = (t) => (t || '').replace(/\{(first_name|last_name|title|full_name|company|deal_name|anrede)\}/g, (_, k) => SAMPLE_CTX[k] || '');

// Max's official signature is appended automatically to sequence emails — detect pasted copies
const SEQ_SIG_LINE_RES = [
  /^maximilian gorzelitz\b/i,
  /^olta nachfolge gmbh$/i,
  /annastra(ß|ss)e\s*58/i,
  /^52062\s+aachen(,?\s*germany)?$/i,
  /5570\s*38\d\d/,
  /^(https?:\/\/)?www\.olta-nachfolge\.com\/?$/i,
  /^[-–_]{2,}$/,
  /this message and its contents are confidential/i,
  /any disclosure, use, copying or distribution/i,
];
const hasPastedSig = (t) => /annastra(ß|ss)e/i.test(t || '') || /5570\s*38\d\d/.test(t || '');
const stripSigText = (t) => {
  const out = [];
  let prevBlank = true;
  (t || '').replace(/\r\n/g, '\n').split('\n').forEach(raw => {
    const line = raw.trim();
    if (line && SEQ_SIG_LINE_RES.some(re => re.test(line))) return;
    const blank = !line;
    if (blank && prevBlank) return;
    out.push(raw);
    prevBlank = blank;
  });
  while (out.length && !out[out.length - 1].trim()) out.pop();
  return out.join('\n');
};

const EmailStepPreview = ({ step }) => {
  const lines = renderSample(step.template).replace(/\r\n/g, '\n').split('\n');
  return (
    <div data-testid="seq-step-preview" className="w-0 min-w-full">
      <p className="text-[10px] text-muted-foreground mb-1.5">
        This is what the owner receives — rendered with sample data (Erika Mustermann), sent from the enroller's mailbox with Max's signature.
      </p>
      <div className="bg-white text-zinc-900 rounded-sm border border-border p-4">
        <div className="text-[11px] text-zinc-500 border-b border-zinc-200 pb-2 mb-3 break-words">
          <p><span className="font-medium text-zinc-700">Subject:</span> {renderSample(step.subject) || <span className="italic">no subject</span>}</p>
        </div>
        <div className="text-[13px] break-words" style={{ fontFamily: 'Calibri, Arial, Helvetica, sans-serif', lineHeight: 1.35 }}>
          {lines.map((l, i) => <p key={i} style={{ margin: 0 }}>{l || '\u00a0'}</p>)}
          {!hasPastedSig(step.template) && <SignaturePreview name="Maximilian Gorzelitz" phone="+49 241 5570 3831" />}
        </div>
      </div>
    </div>
  );
};

function StepEditor({ step, idx, channels, onChange, onMove, onRemove, count }) {
  const Icon = CHANNEL_ICONS[step.channel] || Mail;
  const [preview, setPreview] = useState(false);
  const isEmail = step.channel === 'email';
  return (
    <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2" data-testid={`seq-step-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`flex items-center gap-1.5 text-[10px] font-mono border rounded px-1.5 py-0.5 ${CHANNEL_COLORS[step.channel] || ''}`}>
          <Icon className="w-3 h-3" /> Step {idx + 1}
        </span>
        <select value={step.channel} onChange={e => onChange(idx, { channel: e.target.value })}
          data-testid={`seq-step-channel-${idx}`}
          className="text-xs bg-secondary border border-border text-foreground rounded-sm px-2 h-7 cursor-pointer focus:outline-none">
          {channels.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <span className="text-[10px] text-muted-foreground">{idx === 0 ? 'starts on day' : 'days after previous step'}</span>
        <Input type="number" min="0" max="365" value={step.delay_days}
          onChange={e => onChange(idx, { delay_days: e.target.value })}
          data-testid={`seq-step-delay-${idx}`}
          className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs w-16" />
        <span className="ml-auto flex items-center gap-1.5">
          {isEmail && (
            <label className={`flex items-center gap-1 text-[10px] rounded-sm border px-1.5 py-0.5 cursor-pointer transition-colors ${step.approve_required ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}
              title="Diese Mail wartet vor dem Versand auf eine Freigabe (Push + Aufgabe einen Werktag vorher)" data-testid={`seq-step-approve-${idx}`}>
              <Checkbox checked={!!step.approve_required} onCheckedChange={v => onChange(idx, { approve_required: !!v })} className="h-3 w-3" data-testid={`seq-step-approve-toggle-${idx}`} />
              <ShieldCheck className="w-3 h-3" /> Freigabe nötig
            </label>
          )}
          {isEmail && (
            <button onClick={() => setPreview(p => !p)} data-testid={`seq-step-preview-toggle-${idx}`}
              className={`flex items-center gap-1 text-[10px] rounded-sm border px-1.5 py-0.5 transition-colors ${preview ? 'border-sky-500/50 bg-sky-500/10 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              {preview ? <><Pencil className="w-3 h-3" /> Edit</> : <><Eye className="w-3 h-3" /> Preview</>}
            </button>
          )}
          <button onClick={() => onMove(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => onMove(idx, 1)} disabled={idx === count - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5"><ChevronDown className="w-3.5 h-3.5" /></button>
          <button onClick={() => onRemove(idx)} data-testid={`seq-step-remove-${idx}`} className="text-muted-foreground hover:text-red-400 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
        </span>
      </div>
      {isEmail && preview ? (
        <EmailStepPreview step={step} />
      ) : (
        <>
          {isEmail && (
            <Input value={step.subject} onChange={e => onChange(idx, { subject: e.target.value })}
              placeholder="Email subject — e.g. Nachfolge bei {company}"
              data-testid={`seq-step-subject-${idx}`}
              className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
          )}
          <Textarea value={step.template} onChange={e => onChange(idx, { template: e.target.value })}
            rows={step.channel === 'call' ? 2 : 5} data-testid={`seq-step-template-${idx}`}
            placeholder="Message template…"
            className="bg-secondary border-border text-foreground rounded-sm text-xs" />
          {isEmail && hasPastedSig(step.template) && (
            <div className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2.5 py-2"
              data-testid={`seq-step-signature-hint-${idx}`}>
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span className="flex-1">
                Max's official OLTA signature is appended automatically to sequence emails — the signature in this text would appear twice.
              </span>
              <button onClick={() => { onChange(idx, { template: stripSigText(step.template) }); toast.success('Pasted signature removed — the official one is appended on send'); }}
                data-testid={`seq-step-strip-signature-btn-${idx}`}
                className="shrink-0 flex items-center gap-1 text-[11px] font-medium border border-amber-500/60 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-sm px-2 py-1 transition-colors">
                <Wand2 className="w-3 h-3" /> Remove pasted signature
              </button>
            </div>
          )}
          {isEmail && (
            <p className="text-[10px] text-muted-foreground">
              Max's official OLTA signature (Maximilian Gorzelitz, address, +49 241 5570 3831, website + confidentiality note) is appended automatically — end the text with the greeting line.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function TemplateCard({ tpl, channels, placeholders, onSaved, onDeleted }) {
  const [name, setName] = useState(tpl.name);
  const [steps, setSteps] = useState(tpl.steps);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const change = (idx, patch) => setSteps(s => s.map((st, i) => i === idx ? { ...st, ...patch } : st));
  const move = (idx, dir) => setSteps(s => {
    const arr = [...s]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });
  const remove = (idx) => setSteps(s => s.filter((_, i) => i !== idx));
  const add = () => setSteps(s => [...s, { channel: 'email', delay_days: 7, subject: '', template: '' }]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.updateSequenceTemplate(tpl.id, { name, steps });
      const n = r.data?.updated_enrollments || 0;
      toast.success(n > 0
        ? `Sequence saved — Texte auf ${n} laufende Enrollment${n === 1 ? '' : 's'} angewendet`
        : 'Sequence saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm(`Delete sequence "${tpl.name}"?`)) return;
    try { await api.deleteSequenceTemplate(tpl.id); toast.success('Sequence deleted'); onDeleted(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveCount = (tpl.steps || []).filter(s => s.channel === 'email' && s.approve_required).length;
  let day = 0;
  const summary = tpl.steps.map((s, i) => { day = i === 0 ? s.delay_days : day + Number(s.delay_days || 0); return `Day ${day} ${s.channel}`; }).join(' → ');

  return (
    <div className="bg-card border border-border rounded-sm" data-testid={`seq-template-${tpl.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-4 text-left">
        <Repeat className="w-4 h-4 text-violet-400 shrink-0" />
        <span className="text-sm font-medium text-foreground">{tpl.name}</span>
        {tpl.prio && <span className={`flex items-center gap-1 text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[tpl.prio]}`} data-testid={`seq-prio-badge-${tpl.prio}`}><Star className="w-2.5 h-2.5" /> PRIO {tpl.prio}</span>}
        {tpl.is_default && <span className="flex items-center gap-1 text-[9px] font-mono text-muted-foreground border border-border bg-secondary/60 rounded px-1.5 py-0.5" title="Nur noch für Deals, die vor der Prio-Umstellung freigegeben wurden" data-testid="seq-legacy-badge">LEGACY — nur Alt-Deals</span>}
        {tpl.prio && tpl.steps.length <= 1 && <span className="text-[9px] text-amber-300/90 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5" data-testid={`seq-prio-empty-${tpl.prio}`}>noch keine Folgeschritte</span>}
        {approveCount > 0 && <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5" title={`${approveCount} E-Mail-Schritt${approveCount === 1 ? '' : 'e'} warten vor dem Versand auf Freigabe`} data-testid={`seq-approve-badge-${tpl.id}`}><ShieldCheck className="w-2.5 h-2.5" /> Freigabe · {approveCount}</span>}
        <span className="text-[10px] text-muted-foreground truncate hidden sm:block">{summary}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <Input value={name} onChange={e => setName(e.target.value)} data-testid="seq-template-name-input"
            className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm" />
          <p className="text-[10px] text-muted-foreground flex items-start gap-1.5" data-testid={`seq-approve-hint-${tpl.id}`}>
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span>Freigabe pro E-Mail-Schritt: Markierte Mails warten vor dem Versand — einen Werktag vorher bekommt der Enroller Push + Aufgabe („Meine Aufgaben“), die Mail geht erst nach „Freigeben“ raus (Text dort anpassbar). Unmarkierte Standard-Mails gehen automatisch.</span>
          </p>
          {steps.map((s, i) => (
            <StepEditor key={i} step={s} idx={i} channels={channels} count={steps.length}
              onChange={change} onMove={move} onRemove={remove} />
          ))}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={add} data-testid="seq-add-step-btn"
              className="flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300">
              <Plus className="w-3 h-3" /> Add step
            </button>
            <p className="text-[10px] text-muted-foreground">
              Placeholders: {placeholders.map(p => `{${p}}`).join(' ')}
            </p>
            <span className="flex gap-2">
              {!tpl.is_default && !tpl.prio && (
                <Button variant="outline" size="sm" onClick={del} data-testid="seq-template-delete-btn"
                  className="border-red-600/40 text-red-400 hover:bg-red-600/10 rounded-sm text-xs h-7 px-2.5">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
              <Button size="sm" onClick={save} disabled={saving} data-testid="seq-template-save-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-7 px-3">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const ENROLL_STATUS_STYLES = {
  active: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  paused: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  completed: 'text-sky-300 border-sky-500/40 bg-sky-500/10',
  cancelled: 'text-muted-foreground border-border bg-secondary',
};

function EnrollmentRow({ e, busy, onAction, onStep, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const fmt = (d) => (d ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
  const CH_LABEL = { letter: 'Letter', email: 'Email', linkedin: 'LinkedIn', call: 'Call' };
  const lastDone = [...(e.steps || [])].reverse().find(s => s.status === 'done');
  const isManualPause = (e.paused_reason || '').startsWith('Paused manually');
  const pausedInfo = e.status === 'paused'
    ? `${isManualPause ? '' : 'Auto-paused: '}${e.paused_reason || 'Paused'} · ${lastDone ? `after ${CH_LABEL[lastDone.channel] || lastDone.channel}${lastDone.actual_date ? ` (${fmt(lastDone.actual_date)})` : ''}` : 'before first step'}`
    : '';
  return (
    <div className="px-4 py-2.5 border-b border-border last:border-b-0" data-testid={`seq-enrollment-${e.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-foreground">{e.contact_name}</span>
        <Link to={`/projects/${e.project_id}`} className="text-xs text-sky-400 hover:text-sky-300 truncate" data-testid={`seq-enrollment-deal-${e.id}`}>
          {e.project_name}
        </Link>
        <span className={`text-[9px] font-mono uppercase border rounded px-1.5 py-0.5 ${ENROLL_STATUS_STYLES[e.status] || ENROLL_STATUS_STYLES.cancelled}`}>
          {e.status}
        </span>
        {pausedInfo && (
          <span className="text-[10px] text-amber-300/90 truncate max-w-full sm:max-w-[420px]" title={pausedInfo}
            data-testid={`seq-paused-why-${e.id}`}>
            {pausedInfo}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground truncate hidden sm:block">{e.template_name}</span>
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {busy === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" /> : (
            <>
              {e.status === 'active' && (
                <button onClick={() => onAction(e.id, 'pause')} title="Pause" data-testid={`seq-enrollment-pause-${e.id}`}
                  className="p-1 text-muted-foreground hover:text-amber-300"><Pause className="w-3.5 h-3.5" /></button>
              )}
              {e.status === 'paused' && (
                <button onClick={() => onAction(e.id, 'resume')} title="Resume" data-testid={`seq-enrollment-resume-${e.id}`}
                  className="p-1 text-muted-foreground hover:text-emerald-300"><Play className="w-3.5 h-3.5" /></button>
              )}
              {['active', 'paused'].includes(e.status) && (
                <button onClick={() => onAction(e.id, 'cancel')} title="Stop sequence" data-testid={`seq-enrollment-cancel-${e.id}`}
                  className="p-1 text-muted-foreground hover:text-red-400"><XCircle className="w-3.5 h-3.5" /></button>
              )}
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {e.steps.map((s, i) => {
          const Icon = CHANNEL_ICONS[s.channel] || Mail;
          const isCurrent = e.status !== 'completed' && i === e.current_step && s.status === 'pending';
          const cls = s.status === 'done'
            ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
            : s.status === 'skipped'
              ? 'text-muted-foreground/60 border-border bg-secondary line-through'
              : isCurrent
                ? 'text-amber-300 border-amber-500/50 bg-amber-500/10'
                : 'text-muted-foreground/70 border-border bg-secondary/50';
          return (
            <button key={i} onClick={() => setExpanded(x => !x)}
              className={`flex items-center gap-1 text-[10px] font-mono border rounded px-1.5 py-0.5 cursor-pointer hover:ring-1 hover:ring-border ${cls}`}
              title={`Step ${i + 1}: ${s.channel}${s.actual_date ? ` — done ${s.actual_date}` : s.scheduled_date ? ` — due ${s.scheduled_date}` : ''} — click to view content`}>
              <Icon className="w-3 h-3" />
              {s.status === 'done' ? fmt(s.actual_date) : isCurrent ? `→ ${fmt(s.scheduled_date) || 'due'}` : `+${s.delay_days}d`}
            </button>
          );
        })}
        <button onClick={() => setExpanded(x => !x)} data-testid={`seq-enrollment-expand-${e.id}`}
          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 ml-1">
          content <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="mt-2.5 pl-1" data-testid={`seq-enrollment-steps-${e.id}`}>
          {e.steps.map((s, i) => (
            <StepRow key={i} step={s} idx={i} isCurrent={i === e.current_step && e.status !== 'completed'}
              active={e.status === 'active'} enrollmentId={e.id} onChanged={onChanged} approveEmails={e.approve_emails}
              onDone={() => onStep(e.id, i, 'done')} onSkip={() => onStep(e.id, i, 'skip')} />
          ))}
        </div>
      )}
      {e.status === 'paused' && e.last_reply && (
        <div className="mt-1.5 border border-amber-500/30 bg-amber-500/5 rounded-sm px-2.5 py-1.5" data-testid={`seq-reply-${e.id}`}>
          <p className="text-[10px] font-medium text-amber-200 flex items-center gap-1.5 flex-wrap">
            {e.last_reply.ai_class && (
              <span data-testid={`seq-reply-class-${e.id}`}
                className={`px-1.5 py-px rounded-full border text-[9px] font-mono uppercase tracking-wide ${
                  { interesse: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
                    absage: 'text-red-300 border-red-500/40 bg-red-500/10',
                    rueckfrage: 'text-sky-300 border-sky-500/40 bg-sky-500/10',
                    abwesenheit: 'text-zinc-300 border-zinc-500/40 bg-zinc-500/10',
                  }[e.last_reply.ai_class] || 'text-zinc-300 border-zinc-500/40 bg-zinc-500/10'}`}>
                {{ interesse: 'Interesse', absage: 'Absage', rueckfrage: 'Rückfrage', abwesenheit: 'Abwesenheit' }[e.last_reply.ai_class] || 'Neutral'}
              </span>
            )}
            {e.last_reply.auto_reply ? 'Auto-Reply' : 'Antwort'} vom {fmt(e.last_reply.ts?.slice(0, 10) || '')}: „{e.last_reply.subject}"
          </p>
          {e.last_reply.ai_summary ? (
            <p className="text-[10px] text-foreground/80 mt-0.5" data-testid={`seq-reply-summary-${e.id}`}>{e.last_reply.ai_summary}</p>
          ) : e.last_reply.snippet && (
            <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`seq-reply-snippet-${e.id}`}>{e.last_reply.snippet}</p>
          )}
          <div className="flex gap-3 mt-1.5">
            <Link to={`/projects/${e.project_id}`} data-testid={`seq-reply-deal-${e.id}`}
              className="text-[10px] text-emerald-300 hover:text-emerald-200">Zum Deal → antworten</Link>
            {!e.last_reply.auto_reply && (
              <button onClick={() => onAction(e.id, 'mark-auto-reply')} data-testid={`seq-mark-auto-${e.id}`}
                className="text-[10px] text-sky-300 hover:text-sky-200">War nur Auto-Reply → Sequenz fortsetzen</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SequenceSettingsPage() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [sendTime, setSendTime] = useState('09:00');
  const [savingTime, setSavingTime] = useState(false);
  const [cc, setCc] = useState([]);
  const [savingCc, setSavingCc] = useState(false);
  const [enrollments, setEnrollments] = useState(null);
  const [showFinished, setShowFinished] = useState(false);
  const [busyEid, setBusyEid] = useState('');
  const [busyShift, setBusyShift] = useState(false);

  const loadEnrollments = useCallback(() => {
    api.getAllSequenceEnrollments().then(r => setEnrollments(r.data)).catch(() => setEnrollments([]));
  }, []);

  const load = useCallback(() => {
    api.getSequenceTemplates()
      .then(r => { setData(r.data); setSendTime(r.data.settings.send_time); setCc(r.data.settings.default_cc || []); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load'));
  }, []);
  useEffect(() => { load(); loadEnrollments(); }, [load, loadEnrollments]);

  const enrollmentAction = async (eid, action) => {
    setBusyEid(eid);
    try {
      if (action === 'pause') await api.pauseEnrollment(eid);
      else if (action === 'resume') await api.resumeEnrollment(eid);
      else if (action === 'mark-auto-reply') {
        await api.markEnrollmentAutoReply(eid);
        toast.success('Als Auto-Reply markiert — Sequenz läuft weiter');
      }
      else if (action === 'cancel') {
        if (!window.confirm('Stop this sequence for good? Remaining steps will not run.')) { setBusyEid(''); return; }
        await api.cancelEnrollment(eid);
      }
      loadEnrollments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusyEid('');
  };

  const shiftLetters = async () => {
    if (!window.confirm('Alle noch offenen Brief-Schritte dieser Woche auf den kommenden Mittwoch verschieben? Briefe gehen danach immer mittwochs raus.')) return;
    setBusyShift(true);
    try {
      const r = await api.shiftLettersToWednesday();
      const n = r.data?.moved || 0;
      const t = r.data?.target ? new Date(r.data.target + 'T00:00:00').toLocaleDateString('de-DE') : '';
      toast.success(n > 0 ? `${n} Brief-Schritt${n === 1 ? '' : 'e'} auf Mittwoch ${t} verschoben` : `Keine offenen Brief-Schritte vor Mittwoch ${t}`);
      loadEnrollments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Verschieben fehlgeschlagen'); }
    setBusyShift(false);
  };

  const stepAction = async (eid, idx, kind) => {
    try {
      if (kind === 'done') await api.completeSequenceStep(eid, idx);
      else await api.skipSequenceStep(eid, idx);
      toast.success(kind === 'done' ? 'Step completed' : 'Step skipped');
      loadEnrollments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveTime = async () => {
    setSavingTime(true);
    try { await api.saveSequenceSettings({ send_time: sendTime, default_cc: cc }); toast.success('Send time saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSavingTime(false);
  };

  const saveCc = async () => {
    setSavingCc(true);
    try { await api.saveSequenceSettings({ send_time: sendTime, default_cc: cc }); toast.success('Default CC saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSavingCc(false);
  };

  const newTemplate = async () => {
    try {
      await api.createSequenceTemplate({
        name: 'New Sequence',
        steps: [{ channel: 'letter', delay_days: 0, subject: '', template: '' }],
      });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up max-w-4xl" data-testid="sequence-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Outreach Sequences</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Multi-channel cadences — enroll contacts on the deal page. Emails send automatically, letters/LinkedIn/calls become tasks.
          </p>
        </div>
        <Button size="sm" onClick={newTemplate} data-testid="seq-new-template-btn"
          className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8 px-3">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Sequence
        </Button>
      </div>

      <StalledOutreachCard onChanged={loadEnrollments} />

      <div className="bg-card border border-border rounded-sm" data-testid="seq-enrollments-card">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
          <Repeat className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-medium text-foreground">Running sequences</span>
          <span className="text-[10px] text-muted-foreground">
            {enrollments === null ? '' : `${enrollments.filter(x => x.status === 'active').length} active · ${enrollments.filter(x => x.status === 'paused').length} paused`}
          </span>
          <span className="ml-auto flex items-center gap-3">
            {isAdmin && (
              <button onClick={shiftLetters} disabled={busyShift} data-testid="seq-shift-wednesday-btn"
                title="Verschiebt alle noch offenen Brief-Schritte dieser Woche auf den kommenden Mittwoch (Briefe gehen immer mittwochs raus)."
                className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200 border border-amber-500/40 bg-amber-500/10 rounded-sm px-2 py-1 disabled:opacity-50">
                {busyShift ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />} Briefe auf Mittwoch
              </button>
            )}
            {enrollments?.some(x => ['completed', 'cancelled'].includes(x.status)) && (
              <button onClick={() => setShowFinished(f => !f)} data-testid="seq-enrollments-toggle-finished"
                className="text-[11px] text-muted-foreground hover:text-foreground">
                {showFinished ? 'Hide finished' : `Show finished (${enrollments.filter(x => ['completed', 'cancelled'].includes(x.status)).length})`}
              </button>
            )}
          </span>
        </div>
        {enrollments === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : (() => {
          const visible = enrollments.filter(x => showFinished || ['active', 'paused'].includes(x.status));
          return visible.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-5 text-center" data-testid="seq-enrollments-empty">
              No running sequences yet. Enroll an owner on the deal page (Sequence button) — letters marked as sent auto-enroll their contact.
            </p>
          ) : visible.map(e => <EnrollmentRow key={e.id} e={e} busy={busyEid} onAction={enrollmentAction} onStep={stepAction} onChanged={loadEnrollments} />);
        })()}
      </div>

      <div className="bg-card border border-border rounded-sm p-4 flex items-center gap-3 flex-wrap" data-testid="seq-settings-card">
        <Clock className="w-4 h-4 text-sky-400" />
        <span className="text-sm text-foreground">Automatic emails send at</span>
        <Input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)}
          data-testid="seq-sendtime-input"
          className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-28" />
        <span className="text-[10px] text-muted-foreground">Europe/Berlin — from the mailbox of whoever enrolled the contact</span>
        <Button size="sm" onClick={saveTime} disabled={savingTime} data-testid="seq-sendtime-save-btn"
          className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-xs h-7 px-3 ml-auto">
          {savingTime ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 space-y-2" data-testid="seq-cc-card">
        <div className="flex items-center gap-2 flex-wrap">
          <Users className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-foreground">Default CC for sequence emails</span>
          <span className="text-[10px] text-muted-foreground">applied to auto-started sequences · prefilled when enrolling manually</span>
          <Button size="sm" onClick={saveCc} disabled={savingCc} data-testid="seq-cc-save-btn"
            className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-xs h-7 px-3 ml-auto">
            {savingCc ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </Button>
        </div>
        <CcPicker value={cc} onChange={setCc} />
      </div>

      <div className="space-y-3" data-testid="seq-prio-templates">
        <div className="text-[11px] text-muted-foreground border border-border rounded-sm p-3 bg-secondary/20" data-testid="seq-prio-intro">
          <strong className="text-foreground/80">Prio-Sequenzen:</strong> Wird ein Brief als versendet markiert, läuft der Deal automatisch in die Sequenz seiner Prio (A/B/C).
          Deals, die vor der Prio-Umstellung{data.prio_since ? ` (${new Date(data.prio_since).toLocaleDateString('de-DE')})` : ''} freigegeben wurden, bleiben in der Legacy-Default-Sequenz.
          Eine Prio-Sequenz ohne Folgeschritte startet nichts — bitte Schritte ergänzen.
        </div>
        {data.templates.filter(t => t.prio).map(t => (
          <TemplateCard key={t.id} tpl={t} channels={data.channels} placeholders={data.placeholders}
            onSaved={load} onDeleted={load} />
        ))}
        {data.templates.filter(t => !t.prio).map(t => (
          <TemplateCard key={t.id} tpl={t} channels={data.channels} placeholders={data.placeholders}
            onSaved={load} onDeleted={load} />
        ))}
      </div>

      <div className="border border-border rounded-sm p-3 bg-secondary/20 text-[11px] text-muted-foreground space-y-1">
        <p><strong className="text-foreground/70">How it works:</strong> A sequence can only start when the contact has all required data (address for letters, email, LinkedIn URL, phone).</p>
        <p>Letters, LinkedIn requests and calls create a task with the prepared text — the next step's timer starts when you mark it done. Emails send automatically at the configured time.</p>
        <p>If the contact replies (email sync) or a call/meeting is logged on the deal, the sequence pauses automatically and you get a push notification.</p>
      </div>
    </div>
  );
}
